import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { logGaEvent } from '../../lib/analytics'

type PrimaryNeed = 'confidence' | 'behavior' | 'communication' | 'direction'
type Breakdown = 'before' | 'ground' | 'saddle' | 'pressure'
type RiderResponse = 'freeze' | 'push' | 'quit' | 'guess'
type SupportStyle = 'app_plan' | 'diy' | 'live' | 'private'
type CoachingLocation = 'in_person' | 'remote'

export const HORSE_RIDER_ASSESSMENT_START_EVENT = 'horse-rider-assessment:start'

interface AssessmentAnswers {
  primaryNeed?: PrimaryNeed
  breakdown?: Breakdown
  riderResponse?: RiderResponse
  supportStyle?: SupportStyle
  coachingLocation?: CoachingLocation
}

interface Option<Value extends string> {
  value: Value
  label: string
  detail: string
}

interface Question<Value extends string> {
  key: keyof AssessmentAnswers
  eyebrow: string
  prompt: string
  options: Option<Value>[]
}

interface AssessmentResult {
  factor: 'Horse' | 'Human' | 'Connection' | 'Environment'
  title: string
  summary: string
  firstMove: string
  avoid: string
  recommendation: string
  ctaLabel: string
  route: string
}

const questions: Question<PrimaryNeed | Breakdown | RiderResponse | SupportStyle>[] = [
  {
    key: 'primaryNeed',
    eyebrow: 'What is happening?',
    prompt: 'Which problem is costing you the most confidence right now?',
    options: [
      { value: 'confidence', label: 'I lose confidence', detail: 'I hesitate, tense up, or avoid situations I used to handle.' },
      { value: 'behavior', label: 'My horse reacts', detail: 'Spooking, rushing, refusing, pinning ears, or another repeated behavior.' },
      { value: 'communication', label: 'We are not connecting', detail: 'My cues feel inconsistent and I cannot tell what my horse needs.' },
      { value: 'direction', label: 'I do not have a plan', detail: 'I need to know what to work on and in what order.' },
    ],
  },
  {
    key: 'breakdown',
    eyebrow: 'Where does it show up?',
    prompt: 'When does the problem usually begin?',
    options: [
      { value: 'before', label: 'Before I even ride', detail: 'Anticipation starts at the barn, gate, trailer, or mounting block.' },
      { value: 'ground', label: 'During groundwork', detail: 'Leading, yielding, standing, or personal-space work becomes unclear.' },
      { value: 'saddle', label: 'Once I am riding', detail: 'The issue appears when I ask for direction, speed, or a transition.' },
      { value: 'pressure', label: 'When the environment changes', detail: 'It falls apart away from home, near distractions, or under pressure.' },
    ],
  },
  {
    key: 'riderResponse',
    eyebrow: 'What happens next?',
    prompt: 'What do you tend to do in that moment?',
    options: [
      { value: 'freeze', label: 'I freeze or hesitate', detail: 'I know I should act, but I stop trusting my decision.' },
      { value: 'push', label: 'I add more pressure', detail: 'I repeat the cue harder and the situation usually gets bigger.' },
      { value: 'quit', label: 'I end the session', detail: 'Stopping feels safer, but the same problem returns next time.' },
      { value: 'guess', label: 'I try different things', detail: 'I have plenty of advice, but no reliable system for choosing.' },
    ],
  },
  {
    key: 'supportStyle',
    eyebrow: 'What would help?',
    prompt: 'Which kind of support would you realistically use?',
    options: [
      { value: 'app_plan', label: 'A plan I can use at the barn', detail: 'Ongoing guidance that adjusts as my horse and goal change.' },
      { value: 'diy', label: 'A self-paced program', detail: 'A complete system I can learn and follow on my own schedule.' },
      { value: 'live', label: 'Live online coaching', detail: 'A structured group with feedback and accountability from Scott.' },
      { value: 'private', label: 'Scott working directly with me', detail: 'Private help focused on my horse, my riding, and my situation.' },
    ],
  },
]

const privateLocationQuestion: Question<CoachingLocation> = {
  key: 'coachingLocation',
  eyebrow: 'One last detail',
  prompt: 'How would you need to work with Scott?',
  options: [
    { value: 'in_person', label: 'In person', detail: 'I can bring my horse to Scott or arrange direct, hands-on work.' },
    { value: 'remote', label: 'By Zoom', detail: 'I need private coaching that works wherever I live.' },
  ],
}

type CompletedAssessmentAnswers = Required<Omit<AssessmentAnswers, 'coachingLocation'>> &
  Pick<AssessmentAnswers, 'coachingLocation'>

function getResult(answers: CompletedAssessmentAnswers): AssessmentResult {
  const factor = answers.breakdown === 'pressure'
    ? 'Environment'
    : answers.primaryNeed === 'confidence' || answers.riderResponse === 'freeze'
      ? 'Human'
      : answers.primaryNeed === 'behavior'
        ? 'Horse'
        : 'Connection'

  const profiles: Record<AssessmentResult['factor'], Pick<AssessmentResult, 'title' | 'summary' | 'firstMove' | 'avoid'>> = {
    Human: {
      title: 'The Confidence Loop',
      summary: 'Your biggest obstacle is not a lack of courage. The moment feels uncertain, your body braces, and your horse responds to that change. That reaction then confirms the worry you started with.',
      firstMove: 'Choose one situation and define the smallest successful version of it before you begin.',
      avoid: 'Do not wait to feel completely confident before giving your horse a clear answer.',
    },
    Horse: {
      title: 'The Behavior Clue',
      summary: 'The behavior is information, not the whole problem. Your horse is showing where understanding, regulation, or preparation breaks down. Correcting only the visible reaction leaves the cause untouched.',
      firstMove: 'Notice the first subtle change that happens before the obvious behavior appears.',
      avoid: 'Do not make the correction bigger until you understand what the first response is telling you.',
    },
    Connection: {
      title: 'The Communication Gap',
      summary: 'You and your horse are both responding, but the meaning of the conversation changes from moment to moment. More cues will not fix that. A repeatable sequence and a clear release will.',
      firstMove: 'Use one cue, one response standard, and one consistent release in your next session.',
      avoid: 'Do not cycle through several techniques before your horse has a fair chance to understand one.',
    },
    Environment: {
      title: 'The Context Gap',
      summary: 'The skill may exist at home, but it has not become dependable when the surroundings add pressure. The answer is not avoiding every difficult setting or jumping straight into the hardest one.',
      firstMove: 'Recreate the challenge at a lower intensity where you can both stay responsive.',
      avoid: 'Do not measure success only by whether your horse completes the task in the hardest environment.',
    },
  }

  if (answers.supportStyle === 'private' && answers.coachingLocation === 'remote') {
    return {
      factor,
      ...profiles[factor],
      recommendation: 'Private Zoom coaching is the strongest fit because you want Scott focused on your specific situation and need that support to work wherever you live.',
      ctaLabel: 'Ask About Zoom Coaching',
      route: '/contact?interest=zoom-coaching',
    }
  }

  if (answers.supportStyle === 'private') {
    return {
      factor,
      ...profiles[factor],
      recommendation: 'The Riding Intensive is the strongest fit because Scott can observe you and your horse directly, identify the first breakdown, and build the next steps around what he sees.',
      ctaLabel: 'Explore the Riding Intensive',
      route: '/private-sessions',
    }
  }

  if (answers.supportStyle === 'live') {
    const horseSenseFit = answers.primaryNeed === 'behavior' || answers.primaryNeed === 'communication'
    return {
      factor,
      ...profiles[factor],
      recommendation: horseSenseFit
        ? 'Horse Sense Coaching is the strongest fit because you want live help understanding the pattern and making better technical decisions as it changes.'
        : 'The Confidence Accelerator is the strongest fit because you want live coaching, a structured process, and accountability around your actual situation.',
      ctaLabel: horseSenseFit ? 'Explore Horse Sense' : 'Explore the Accelerator',
      route: horseSenseFit ? '/horse-sense' : '/accelerator',
    }
  }

  if (answers.supportStyle === 'diy') {
    const diyFit = answers.primaryNeed === 'confidence'
      ? {
          recommendation: 'The Confident Equestrian Foundation Guide is the strongest fit because it gives you a complete confidence system you can work through on your own schedule.',
          ctaLabel: 'Explore the Confidence Guide',
          route: '/programs/confident-equestrian',
        }
      : answers.primaryNeed === 'behavior' || answers.breakdown === 'ground'
        ? {
            recommendation: 'The Groundwork Foundation Guide is the strongest fit because it gives you a structured, self-paced way to rebuild communication and dependable responses from the ground.',
            ctaLabel: 'Explore the Groundwork Guide',
            route: '/programs/groundwork-foundation-guide',
          }
        : answers.primaryNeed === 'communication'
          ? {
              recommendation: 'The Rider’s Foundation Guide is the strongest fit because it gives you the core communication framework to study and apply at your own pace.',
              ctaLabel: 'Explore the Rider’s Foundation Guide',
              route: '/programs/riders-foundation-guide',
            }
          : {
              recommendation: 'A self-paced Advantage Horsemanship program is the strongest fit because you want a complete system you can follow independently and keep for future reference.',
              ctaLabel: 'Compare Self-Paced Programs',
              route: '/programs',
            }

    return {
      factor,
      ...profiles[factor],
      ...diyFit,
    }
  }

  return {
    factor,
    ...profiles[factor],
    recommendation: "The Rider's Way is the strongest fit: a practical plan you can take to the barn, guided rides, and coaching built around your horse.",
    ctaLabel: "Explore Rider's Way",
    route: 'https://riderswayapp.com',
  }
}

export function HorseRiderAssessment() {
  const [started, setStarted] = useState(false)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<AssessmentAnswers>({})
  const [result, setResult] = useState<AssessmentResult | null>(null)

  const activeQuestions = answers.supportStyle === 'private'
    ? [...questions, privateLocationQuestion]
    : questions
  const question = activeQuestions[questionIndex]
  const selectedValue = question ? answers[question.key] : undefined

  useEffect(() => {
    function beginFromPageEntry(): void {
      setStarted((current) => {
        if (current) return current
        logGaEvent('assessment_start', {
          assessment_name: 'horse_rider_starting_point',
          start_method: 'page_cta',
        })
        return true
      })
    }

    window.addEventListener(HORSE_RIDER_ASSESSMENT_START_EVENT, beginFromPageEntry)
    if (window.location.hash === '#horse-rider-assessment') beginFromPageEntry()

    return () => {
      window.removeEventListener(HORSE_RIDER_ASSESSMENT_START_EVENT, beginFromPageEntry)
    }
  }, [])

  function startAssessment() {
    setStarted(true)
    logGaEvent('assessment_start', { assessment_name: 'horse_rider_starting_point' })
  }

  function selectAnswer(value: string) {
    if (!question) return
    setAnswers((current) => ({ ...current, [question.key]: value }))
    logGaEvent('assessment_answer', {
      assessment_name: 'horse_rider_starting_point',
      question_name: question.key,
      answer_value: value,
    })
  }

  function continueAssessment() {
    if (!question || !selectedValue) return

    if (questionIndex < activeQuestions.length - 1) {
      setQuestionIndex((current) => current + 1)
      return
    }

    const completedAnswers = answers as CompletedAssessmentAnswers
    const nextResult = getResult(completedAnswers)
    setResult(nextResult)
    logGaEvent('assessment_complete', {
      assessment_name: 'horse_rider_starting_point',
      result_factor: nextResult.factor.toLowerCase(),
      recommendation: nextResult.route,
    })
  }

  function restartAssessment() {
    setAnswers({})
    setQuestionIndex(0)
    setResult(null)
    logGaEvent('assessment_restart', { assessment_name: 'horse_rider_starting_point' })
  }

  function trackRecommendation() {
    if (!result) return
    logGaEvent('assessment_recommendation_click', {
      assessment_name: 'horse_rider_starting_point',
      result_factor: result.factor.toLowerCase(),
      destination: result.route,
    })
  }

  return (
    <section id="horse-rider-assessment" className="bg-[#F7F4EF] py-16 lg:py-24 px-5 sm:px-8 scroll-mt-6">
      <div className="max-w-3xl mx-auto">
        {!started ? (
          <div className="text-center">
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-gold mb-4">
              A clearer starting point
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-semibold leading-tight mb-5">
              Find what is actually getting in the way.
            </h2>
            <p className="font-body text-base text-navy/65 leading-relaxed max-w-xl mx-auto mb-4">
              Four questions will show you whether the first thing to address is the horse, the human, the connection, or the environment.
            </p>
            <p className="font-body text-sm text-navy/45 mb-9">
              About 60 seconds. No email required.
            </p>
            <button
              type="button"
              onClick={startAssessment}
              className="inline-flex min-h-[56px] w-full sm:w-auto items-center justify-center bg-gold px-10 font-body text-sm font-bold uppercase tracking-wider text-navy transition-opacity hover:opacity-90"
            >
              Find My Starting Point
            </button>
          </div>
        ) : result ? (
          <div className="rounded-2xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-10 lg:p-12" aria-live="polite">
            <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
              Your primary factor: {result.factor}
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight text-navy mb-5">
              {result.title}
            </h2>
            <p className="font-body text-base leading-relaxed text-navy/70 mb-8">
              {result.summary}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="rounded-xl border-l-4 border-gold bg-[#F7F4EC] p-5">
                <p className="font-body text-xs font-bold uppercase tracking-wider text-navy mb-2">
                  Your first move
                </p>
                <p className="font-body text-sm leading-relaxed text-navy/70">{result.firstMove}</p>
              </div>
              <div className="rounded-xl border-l-4 border-navy/25 bg-[#F7F4EC] p-5">
                <p className="font-body text-xs font-bold uppercase tracking-wider text-navy mb-2">
                  What to avoid
                </p>
                <p className="font-body text-sm leading-relaxed text-navy/70">{result.avoid}</p>
              </div>
            </div>

            <div className="border-t border-navy/15 pt-7">
              <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-gold mb-2">
                Best next step
              </p>
              <p className="font-body text-sm sm:text-base leading-relaxed text-navy/70 mb-6">
                {result.recommendation}
              </p>
              {result.route.startsWith('http') ? (
                <a
                  href={result.route}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackRecommendation}
                  className="inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-gold px-9 font-body text-sm font-bold uppercase tracking-wider text-navy transition-opacity hover:opacity-90 sm:w-auto"
                >
                  {result.ctaLabel}
                </a>
              ) : (
                <Link
                  to={result.route}
                  onClick={trackRecommendation}
                  className="inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-navy px-9 font-body text-sm font-bold uppercase tracking-wider text-white transition-opacity hover:opacity-90 sm:w-auto"
                >
                  {result.ctaLabel}
                </Link>
              )}
              <button
                type="button"
                onClick={restartAssessment}
                className="mt-3 inline-flex min-h-[52px] w-full items-center justify-center font-body text-sm font-semibold text-navy/55 underline underline-offset-4 sm:ml-5 sm:mt-0 sm:w-auto"
              >
                Retake the questions
              </button>
            </div>
          </div>
        ) : question ? (
          <div className="rounded-2xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-10 lg:p-12">
            <div className="mb-8" aria-label={`Question ${questionIndex + 1} of ${activeQuestions.length}`}>
              <div className="mb-3 flex items-center justify-between gap-4">
                <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-gold">
                  Question {questionIndex + 1} of {activeQuestions.length}
                </p>
                <p className="font-body text-xs text-navy/45">{question.eyebrow}</p>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-navy/10">
                <div
                  className="h-full bg-gold transition-all"
                  style={{ width: `${((questionIndex + 1) / activeQuestions.length) * 100}%` }}
                />
              </div>
            </div>

            <fieldset>
              <legend className="font-heading text-3xl sm:text-4xl font-semibold leading-tight text-navy mb-7">
                {question.prompt}
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {question.options.map((option) => {
                  const selected = selectedValue === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => selectAnswer(option.value)}
                      aria-pressed={selected}
                    className={`min-h-[88px] rounded-xl border-2 p-4 text-left transition-colors ${
                        selected
                          ? 'border-gold bg-[#F7F4EC]'
                          : 'border-navy/15 bg-white hover:border-navy/40'
                      }`}
                    >
                      <span className="block font-body text-sm font-bold text-navy mb-1">{option.label}</span>
                      <span className="block font-body text-xs leading-relaxed text-navy/55">{option.detail}</span>
                    </button>
                  )
                })}
              </div>
            </fieldset>

            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setQuestionIndex((current) => Math.max(0, current - 1))}
                disabled={questionIndex === 0}
                className="inline-flex min-h-[52px] items-center justify-center px-5 font-body text-sm font-semibold text-navy/55 disabled:invisible"
              >
                Back
              </button>
              <button
                type="button"
                onClick={continueAssessment}
                disabled={!selectedValue}
                className="inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-navy px-9 font-body text-sm font-bold uppercase tracking-wider text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-35 sm:w-auto"
              >
                {questionIndex === activeQuestions.length - 1 ? 'Show My Starting Point' : 'Continue'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
